// Chord and arpeggio type definitions
// This file contains universal chord structures used across all musical contexts

// Advanced chord types and arpeggios for the arpeggio selector
export const ARPEGGIO_TYPES = {
  triads: [
    { id: 'min', name: 'Minor', intervals: [0, 3, 7], symbol: 'm' },
    { id: 'maj', name: 'Major', intervals: [0, 4, 7], symbol: '' },
    { id: 'dim', name: 'Diminished', intervals: [0, 3, 6], symbol: 'dim' },
    { id: 'aug', name: 'Augmented', intervals: [0, 4, 8], symbol: 'aug' },
    { id: 'sus2', name: 'Sus2', intervals: [0, 2, 7], symbol: 'sus2' },
    { id: 'sus4', name: 'Sus4', intervals: [0, 5, 7], symbol: 'sus4' },
    { id: '5', name: 'Power Chord (5)', intervals: [0, 7], symbol: '5' }
  ],
  seventh: [
    { id: 'min7', name: 'Minor 7th', intervals: [0, 3, 7, 10], symbol: 'm7' },
    { id: 'maj7', name: 'Major 7th', intervals: [0, 4, 7, 11], symbol: 'maj7' },
    { id: 'dom7', name: 'Dominant 7th', intervals: [0, 4, 7, 10], symbol: '7' },
    { id: 'min6', name: 'Minor 6th', intervals: [0, 3, 7, 9], symbol: 'm6' },
    { id: 'maj6', name: 'Major 6th', intervals: [0, 4, 7, 9], symbol: '6' },
    { id: 'dim7', name: 'Diminished 7th', intervals: [0, 3, 6, 9], symbol: 'dim7' },
    { id: 'half_dim7', name: 'Half-Diminished 7th', intervals: [0, 3, 6, 10], symbol: 'm7♭5' },
    { id: 'minMaj7', name: 'Minor-Major 7th', intervals: [0, 3, 7, 11], symbol: 'mMaj7' },
    { id: 'augMaj7', name: 'Augmented-Major 7th', intervals: [0, 4, 8, 11], symbol: 'augMaj7' }
  ],
  extended: [
    { id: 'min9', name: 'Minor 9th', intervals: [0, 3, 7, 10, 14], symbol: 'm9' },
    { id: 'maj9', name: 'Major 9th', intervals: [0, 4, 7, 11, 14], symbol: 'maj9' },
    { id: 'dom9', name: 'Dominant 9th', intervals: [0, 4, 7, 10, 14], symbol: '9' },
    { id: 'min11', name: 'Minor 11th', intervals: [0, 3, 7, 10, 14, 17], symbol: 'm11' },
    { id: 'maj11', name: 'Major 11th', intervals: [0, 4, 7, 11, 14, 17], symbol: 'maj11' },
    { id: 'dom11', name: 'Dominant 11th', intervals: [0, 4, 7, 10, 14, 17], symbol: '11' },
    { id: 'min13b', name: 'Minor 13th ♭13', intervals: [0, 3, 7, 10, 14, 17, 20], symbol: 'm13♭' },
    { id: 'min13', name: 'Minor 13th', intervals: [0, 3, 7, 10, 14, 17, 21], symbol: 'm13' },
    { id: 'maj13', name: 'Major 13th', intervals: [0, 4, 7, 11, 14, 17, 21], symbol: 'maj13' },
    { id: 'dom13', name: 'Dominant 13th', intervals: [0, 4, 7, 10, 14, 17, 21], symbol: '13' }
  ],
  added: [
    { id: 'minadd9', name: 'Minor (add9)', intervals: [0, 3, 7, 14], symbol: 'm(add9)' },
    { id: 'majadd9', name: 'Major (add9)', intervals: [0, 4, 7, 14], symbol: 'add9' },
    { id: 'min7add11', name: 'Minor 7 (add11)', intervals: [0, 3, 7, 10, 17], symbol: 'm7(add11)' },
    { id: 'maj7add11', name: 'Major 7 (add11)', intervals: [0, 4, 7, 11, 17], symbol: 'maj7(add11)' },
    { id: 'dom7add11', name: 'Dominant 7 (add11)', intervals: [0, 4, 7, 10, 17], symbol: '7(add11)' },
    { id: 'min7b13', name: 'Minor 7 ♭13', intervals: [0, 3, 7, 10, 20], symbol: 'm7♭13' },
    { id: 'min69', name: 'Minor 6/9', intervals: [0, 3, 7, 9, 14], symbol: 'm6/9' },
    { id: 'maj69', name: 'Major 6/9', intervals: [0, 4, 7, 9, 14], symbol: '6/9' }
  ],
  modal: [
    { id: '7sus4', name: '7sus4', intervals: [0, 5, 7, 10], symbol: '7sus4' },
    { id: 'quartal', name: 'Quartal', intervals: [0, 5, 10], symbol: 'quartal' },
    { id: '5b6', name: '5♭6', intervals: [0, 7, 8], symbol: '5♭6' },
    { id: '7no3', name: '7(no3)', intervals: [0, 7, 10], symbol: '7(no3)' },
    { id: '7add4', name: '7(add4)', intervals: [0, 4, 5, 7, 10], symbol: '7(add4)' }
  ]
};

// Get all arpeggio types flattened into a single array
export const getAllArpeggioTypes = () => {
  return Object.values(ARPEGGIO_TYPES).flat();
};

// Get a specific arpeggio type by ID
export const getArpeggioTypeById = (id) => {
  for (const category of Object.values(ARPEGGIO_TYPES)) {
    const found = category.find(type => type.id === id);
    if (found) return found;
  }
  return null;
};

// Group arpeggios by category
export const groupArpeggiosByCategory = (arpeggios) => {
  const grouped = {
    triads: [],
    seventh: [],
    extended: [],
    added: [],
    modal: []
  };
  
  // Go through each arpeggio and determine its category
  for (const arpeggio of arpeggios) {
    // Find which category this arpeggio belongs to
    for (const [category, types] of Object.entries(ARPEGGIO_TYPES)) {
      if (types.some(type => type.id === arpeggio.id)) {
        grouped[category].push(arpeggio);
        break;
      }
    }
  }
  
  return grouped;
};